import React, { useMemo } from "react";
import {
	Autocomplete,
	Checkbox,
	Chip,
	FormControl,
	FormHelperText,
	TextField,
	Typography,
} from "@mui/material";

const normalizeSearchText = (value = "") =>
	String(value ?? "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.trim();

export const CampanaMultiSelect = ({
	campanas = [],
	value = [],
	onChange = () => {},
	label = "Campañas documentales",
	helperText = "Determinan las carpetas documentales disponibles para esta persona.",
}) => {
	const campanasValidas = useMemo(
		() =>
			Array.isArray(campanas)
				? campanas.filter(
						(campana) =>
							campana &&
							Number(campana.id) > 0 &&
							campana.activa !== false &&
							campana.eliminado !== true
				  )
				: [],
		[campanas]
	);

	const selectedIds = useMemo(
		() =>
			Array.isArray(value)
				? [...new Set(value.map((item) => Number(typeof item === "object" ? item?.id : item)).filter((id) => id > 0))]
				: [],
		[value]
	);
	const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
	const selectedCampanas = useMemo(
		() => campanasValidas.filter((campana) => selectedSet.has(Number(campana.id))),
		[campanasValidas, selectedSet]
	);

	return (
		<FormControl fullWidth>
			<Autocomplete
				multiple
				disableCloseOnSelect
				options={campanasValidas}
				value={selectedCampanas}
				isOptionEqualToValue={(option, selected) => Number(option?.id) === Number(selected?.id)}
				getOptionLabel={(option) => option?.nombre?.trim() || `Campaña ${option?.id ?? ""}`}
				filterOptions={(options, state) => {
					const query = normalizeSearchText(state?.inputValue);
					return query
						? options.filter((option) =>
							normalizeSearchText([option?.nombre, option?.slug, option?.id].filter(Boolean).join(" ")).includes(query)
						  )
						: options;
				}}
				onChange={(_, nextValue) => onChange([...new Set(nextValue.map((item) => Number(item?.id)).filter((id) => id > 0))])}
				renderOption={(props, option, { selected }) => (
					<li {...props} key={`campana-option-${option.id}`}>
						<Checkbox checked={selected} sx={{ mr: 1 }} />
						<div>
							<Typography variant="body1">{option?.nombre?.trim() || `Campaña ${option.id}`}</Typography>
							{option?.descripcion ? <Typography variant="caption" color="text.secondary">{option.descripcion}</Typography> : null}
						</div>
					</li>
				)}
				renderTags={(selectedOptions, getTagProps) =>
					selectedOptions.map((option, index) => {
						const { key, ...tagProps } = getTagProps({ index });
						return <Chip {...tagProps} key={key || `campana-chip-${option.id}`} size="small" label={option?.nombre || `Campaña ${option.id}`} />;
					})
				}
				renderInput={(params) => (
					<TextField {...params} label={label} placeholder={selectedCampanas.length === 0 ? "Buscar campañas..." : ""} />
				)}
				noOptionsText="No hay campañas activas"
			/>
			{helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
		</FormControl>
	);
};

export default CampanaMultiSelect;
